import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Clock, 
  User, 
  Building, 
  RefreshCw,
  Filter,
  Search,
  BarChart3
} from 'lucide-react';
import { 
  getAllSubscriptionActivities, 
  getCompanySubscriptionActivities 
} from '../utils/subscriptionUtils';

const ActivityLogs = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'plan_changed', 'trial_started', 'trial_ended', 'plan_created'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [showUsageTracking, setShowUsageTracking] = useState(false);
  const [usageActivities, setUsageActivities] = useState([]);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    loadActivities();
  }, [filter, selectedCompany]);

  useEffect(() => {
    if (showUsageTracking && selectedCompany) {
      loadUsageActivities();
    }
  }, [showUsageTracking, selectedCompany]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      setLoadError('');
      let activitiesData = [];

      if (selectedCompany) {
        activitiesData = await getCompanySubscriptionActivities(selectedCompany, 100);
      } else {
        activitiesData = await getAllSubscriptionActivities(100);
      }

      setActivities(activitiesData);

      if (showUsageTracking) {
        await loadUsageActivities();
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      setLoadError(error.message || 'Failed to load activities');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsageActivities = async () => {
    try {
      // Import Firebase from the app
      const { getAppFirestore } = await import('../appFirebaseClient');
      const appDb = getAppFirestore();
      const { collection, getDocs, query, orderBy, limit: limitQuery } = await import('firebase/firestore');
      
      let usageLogs = [];
      
      if (selectedCompany) {
        // Get usage alerts for specific company
        const alertsRef = collection(appDb, 'companies', selectedCompany, 'usageAlerts');
        const alertsQuery = query(alertsRef, orderBy('timestamp', 'desc'), limitQuery(50));
        const snapshot = await getDocs(alertsQuery);
        
        snapshot.forEach(doc => {
          usageLogs.push({
            id: doc.id,
            type: 'usage_alert',
            ...doc.data()
          });
        });
      }
      
      setUsageActivities(usageLogs);
    } catch (error) {
      console.error('Error loading usage activities:', error);
    }
  };

  const filteredActivities = activities.filter(activity => {
    const matchesFilter = filter === 'all' || activity.action === filter;
    const matchesSearch = !searchTerm || 
      activity.company_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (activity.details?.plan_name && activity.details.plan_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'plan_changed':
        return <Building className="w-4 h-4 text-blue-600" />;
      case 'trial_started':
        return <Activity className="w-4 h-4 text-green-600" />;
      case 'trial_ended':
        return <Activity className="w-4 h-4 text-red-600" />;
      case 'plan_created':
        return <Building className="w-4 h-4 text-purple-600" />;
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'plan_changed':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'trial_started':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'trial_ended':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'plan_created':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getActionDescription = (activity) => {
    const { action, details } = activity;
    
    switch (action) {
      case 'plan_changed':
        return `Plan changed to ${details.new_plan}`;
      case 'trial_started':
        return `Free trial started (${details.trial_duration_days} days)`;
      case 'trial_ended':
        return 'Free trial ended';
      case 'plan_created':
        return `New plan "${details.plan_name}" created`;
      default:
        return action.replace('_', ' ');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Subscription Activity Logs</h2>
          </div>
          
          <button
            onClick={loadActivities}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {loadError && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            {loadError}
            <p className="text-xs mt-1 text-gray-600">
              Deploy backoffice functions and Firestore rules, then refresh. An empty list is normal if nothing was logged yet.
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Actions</option>
              <option value="plan_changed">Plan Changes</option>
              <option value="trial_started">Trial Started</option>
              <option value="trial_ended">Trial Ended</option>
              <option value="plan_created">Plan Created</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showUsageTracking}
                onChange={(e) => setShowUsageTracking(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium">Show Usage Tracking</span>
            </label>
          </div>
          
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by company ID or action..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
            />
          </div>
        </div>

        {/* Usage Tracking Activities */}
        {showUsageTracking && usageActivities.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Usage Tracking Activities
            </h3>
            <div className="space-y-2">
              {usageActivities.map((activity) => (
                <div
                  key={activity.id}
                  className={`p-3 rounded-lg border text-sm ${
                    activity.type === 'blocked' ? 'bg-red-50 border-red-200' :
                    activity.type === 'critical' ? 'bg-orange-50 border-orange-200' :
                    'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{activity.message}</div>
                      <div className="text-xs opacity-75 mt-1">
                        {activity.featureName}: {activity.current}/{activity.limit} ({activity.percentage}%)
                      </div>
                    </div>
                    <div className="text-xs opacity-75">
                      {activity.timestamp && new Date(activity.timestamp.toDate()).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subscription Activities List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              Loading activities...
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium mb-2">No Activities Found</h3>
              <p>No subscription activities match your current filters.</p>
            </div>
          ) : (
            filteredActivities.map((activity) => (
              <div
                key={activity.id}
                className={`p-4 rounded-lg border ${getActionColor(activity.action)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getActionIcon(activity.action)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">
                          {getActionDescription(activity)}
                        </span>
                        {activity.company_id !== 'system' && (
                          <span className="text-sm opacity-75">
                            (Company: {activity.company_id})
                          </span>
                        )}
                      </div>
                      
                      {activity.details && (
                        <div className="text-sm opacity-75 mb-2">
                          {activity.action === 'plan_changed' && (
                            <div>Features: {Object.keys(activity.details.features || {}).length} categories</div>
                          )}
                          {activity.action === 'trial_started' && (
                            <div>Trial Plan: {activity.details.trial_plan}, Selected Plan: {activity.details.selected_plan}</div>
                          )}
                          {activity.action === 'plan_created' && (
                            <div>Pricing: {activity.details.plan_pricing?.monthly}, Features: {activity.details.plan_features} categories</div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs opacity-75">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(activity.timestamp)}
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {activity.admin_info?.displayName || activity.admin_info?.email || 'Unknown Admin'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary */}
        {filteredActivities.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600">
              Showing {filteredActivities.length} of {activities.length} activities
              {filter !== 'all' && ` (filtered by ${filter})`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;


